import { useFormContext, useWatch } from "react-hook-form";

import {
  Box,
  Checkbox,
  Divider,
  FormCheckbox,
  FormSelect,
  FormTextArea,
  FormTextField,
  Icon,
  Typography,
} from "@luce/ui";

import {
  babysitterTasksOptions,
  healthConditionOptions,
  saveAsOptions,
  type BabyProfileFormValues,
} from "./lib";

const TASK_KEYS = babysitterTasksOptions.map((o) => o.key);

export function BabyProfileFormBody() {
  return (
    <Box className="gap-spacing-md flex flex-col">
      <Box className="gap-spacing-sm flex flex-row items-center">
        <Icon name="ClipboardText" weight="fill" size="md" color="brand-primary" />
        <Typography variant="label-md">Child Profile</Typography>
      </Box>
      <Typography variant="body-md" color="neutral-foreground-intermediate">
        Helps us share your child&apos;s needs and assign the right babysitter.
      </Typography>

      <FormSelect
        name="saveAs"
        label="Save this profile as"
        required
        placeholder="Select profile type"
        options={[...saveAsOptions]}
      />
      <Box className="gap-spacing-md flex flex-col md:flex-row">
        <Box className="flex-1">
          <FormTextField
            name="name"
            label="Child's Name"
            required
            placeholder="Enter the baby's name"
          />
        </Box>
        <Box className="flex-1">
          <FormTextField name="age" label="Age" required placeholder="Enter the age" />
        </Box>
      </Box>

      <Divider size="2pt" />
      <HealthConditionsField />

      <Divider size="2pt" />
      <TasksField />

      <Divider size="2pt" />
      <FormTextArea
        name="routineDetails"
        label="Tell us a little more about your child's needs or routine"
        required
        placeholder="e.g., allergy: peanuts, nap 2–3 pm"
      />
    </Box>
  );
}

/**
 * Health-condition checklist. Legacy carried a "Select All" row here that only opened the
 * sales modal (it never selected anything); the sales nudge now fires on save, so that row
 * is gone. A details field appears once any condition is ticked.
 */
function HealthConditionsField() {
  const { control } = useFormContext<BabyProfileFormValues>();
  const conditions = useWatch({ control, name: "healthConditions" });
  const anySelected = Object.values(conditions ?? {}).some(Boolean);

  return (
    <Box className="gap-spacing-sm flex flex-col">
      <Typography variant="label-sm">
        Does your child currently display any of the following?
      </Typography>
      <Typography variant="body-sm" color="neutral-foreground-intermediate">
        Select all that apply
      </Typography>
      <Box className="flex flex-col">
        {healthConditionOptions.map((option, index) => (
          <Box key={option.key}>
            {index > 0 && <Divider />}
            <Box className="gap-spacing-sm py-spacing-md flex flex-row items-center">
              <FormCheckbox
                size="sm"
                name={`healthConditions.${option.key}`}
                accessibilityLabel={option.label}
              />
              <Typography variant="body-md">{option.label}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
      {anySelected && (
        <Box className="gap-spacing-xs flex flex-col">
          <Typography variant="label-sm">Please provide details about the conditions*</Typography>
          <Typography variant="body-sm" color="neutral-foreground-intermediate">
            Describe the care requirements
          </Typography>
          <FormTextArea
            name="healthConditionsDetails"
            placeholder="e.g., Allergic to peanuts, requires EpiPen nearby"
          />
        </Box>
      )}
    </Box>
  );
}

/** Babysitter-task checklist with captions, a "Select All" control, and array-level error. */
function TasksField() {
  const { control, setValue, formState } = useFormContext<BabyProfileFormValues>();
  const tasks = useWatch({ control, name: "babysitter_tasks" });
  const allSelected = TASK_KEYS.every((key) => tasks?.[key]);
  const someSelected = TASK_KEYS.some((key) => tasks?.[key]);
  const error = formState.errors.babysitter_tasks?.message;

  const toggleAll = (checked: boolean) => {
    setValue(
      "babysitter_tasks",
      Object.fromEntries(
        TASK_KEYS.map((key) => [key, checked]),
      ) as BabyProfileFormValues["babysitter_tasks"],
      { shouldValidate: true, shouldDirty: true },
    );
  };

  return (
    <Box className="gap-spacing-sm flex flex-col">
      <Typography variant="label-sm">
        What assistance does your child require from our babysitters?*
      </Typography>
      <Typography variant="body-sm" color="neutral-foreground-intermediate">
        Select all that apply
      </Typography>
      <Box className="flex flex-col">
        {babysitterTasksOptions.map((option, index) => (
          <Box key={option.key}>
            {index > 0 && <Divider />}
            <Box className="gap-spacing-sm py-spacing-md flex flex-row items-start">
              <FormCheckbox
                size="sm"
                name={`babysitter_tasks.${option.key}`}
                accessibilityLabel={option.label}
              />
              <Box className="flex flex-1 flex-col">
                <Typography variant="body-md">{option.label}</Typography>
                {option.caption && (
                  <Typography variant="body-sm" color="neutral-foreground-intermediate">
                    {option.caption}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
        <Divider />
        <Box className="gap-spacing-sm py-spacing-md flex flex-row items-center">
          <Checkbox
            size="sm"
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onCheckedChange={toggleAll}
            accessibilityLabel="Select All"
          />
          <Typography variant="body-md">Select All</Typography>
        </Box>
      </Box>
      {error && (
        <Box className="gap-spacing-xs flex flex-row items-center">
          <Icon
            name="WarningCircle"
            size={16}
            weight="fill"
            className="text-luce-system-danger-default shrink-0"
          />
          <Typography variant="body-sm" color="system-danger-default">
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
